import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useUser } from '@/context/user-provider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users,
  User,
  Globe,
  Trash2,
  Loader2,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import type { AssetWithPermission, AssetShareInfo } from '@/services/asset-service';
import { assetService } from '@/services/asset-service';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/asset-permission-utils';

interface AssetSharingPanelProps {
  asset: AssetWithPermission;
  onShareUpdate?: () => void;
}

const AssetSharingPanel: React.FC<AssetSharingPanelProps> = ({
  asset,
  onShareUpdate
}) => {
  const { user } = useUser();
  const [sharingInfo, setSharingInfo] = useState<AssetShareInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removingShareId, setRemovingShareId] = useState<string | null>(null); // Using string as targetId is string
  const [shareToRemove, setShareToRemove] = useState<AssetShareInfo | null>(null);
  
  useEffect(() => {
    loadSharingInfo();
  }, [asset.uuid]);

  const loadSharingInfo = async () => {
    setIsLoading(true);
    try {
      const sharing = await assetService.getAssetSharing(asset.uuid);
      setSharingInfo(sharing);
    } catch (error) {
      toast.error('Failed to load sharing information: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Check if a share represents the current user's own owner access
   * Prevents user from removing their own owner relation (would orphan the asset)
   */
  const isOwnOwnerAccess = (share: AssetShareInfo): boolean => {
    if (!user?.id) return false;
    return share.targetType === 'user' && share.targetId === user.id && share.role === 'owner';
  };

  const handleRemoveShareClick = (shareInfo: AssetShareInfo) => {
    // Prevent removing own owner access
    if (isOwnOwnerAccess(shareInfo)) {
      toast.error('Cannot remove your own owner access to the asset');
      return;
    }
    setShareToRemove(shareInfo);
  };

  const handleConfirmRemove = async () => {
    if (!shareToRemove) return;

    // Double-check: prevent removing own owner access
    if (isOwnOwnerAccess(shareToRemove)) {
      toast.error('Cannot remove your own owner access to the asset');
      setShareToRemove(null);
      return;
    }

    const identifier = `${shareToRemove.targetType}-${shareToRemove.targetId}`;
    setRemovingShareId(identifier);
    try {
      await assetService.removeAssetShare(asset.uuid, shareToRemove.targetType, shareToRemove.targetId);
      setSharingInfo(prev => prev.filter(s =>
        !(s.targetType === shareToRemove.targetType && s.targetId === shareToRemove.targetId)
      ));
      toast.success(`Access removed`);
      onShareUpdate?.();
    } catch (error) {
      toast.error('Failed to remove sharing access: ' + (error as Error).message);
    } finally {
      setRemovingShareId(null);
      setShareToRemove(null);
    }
  };

  const handleMakeUnPublic = async () => {
    if (!asset.permission || asset.permission.role !== 'owner') {
      toast.error('Only owners can remove public access');
      return;
    }

    setRemovingShareId('public-__PUBLIC__');
    try {
      await assetService.makeAssetUnPublic(asset.uuid);
      setSharingInfo(prev => prev.filter(s => 
        !(s.targetType === 'public' && s.targetId === '__PUBLIC__')
      ));
      toast.success('Asset is no longer public');
      onShareUpdate?.();
    } catch (error) {
      toast.error('Failed to make asset un-public: ' + (error as Error).message);
    } finally {
      setRemovingShareId(null);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/assets/${asset.uuid}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Shareable link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const getTargetIcon = (type: string) => {
    switch (type) {
      case 'user': return <User className="h-4 w-4" />;
      case 'organization': return <Users className="h-4 w-4" />;
      case 'public': return <Globe className="h-4 w-4" />;
      case 'team': return <Users className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getTargetDisplay = (share: AssetShareInfo) => {
    if (share.targetType === 'public') {
      return 'Public (anyone with link)';
    }
    // Display resolved name from API (user name > email > org name > ID)
    return share.targetName || share.targetId;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Sharing</span>
          {asset.permission && (
            <Badge variant="outline" className={getRoleBadgeColor(asset.permission?.role || 'viewer')}>
              You: {getRoleLabel(asset.permission?.role || 'viewer')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Only show Public Link section if asset is actually public */}
            {sharingInfo.some(s => s.targetType === 'public') ? (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Public Link
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        Public
                      </Badge>
                      {asset.permission?.role === 'owner' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleMakeUnPublic}
                          disabled={removingShareId === 'public-__PUBLIC__'}
                        >
                          {removingShareId === 'public-__PUBLIC__' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Make Private'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-muted p-2 rounded-md text-sm truncate">
                      {`${window.location.origin}/assets/${asset.uuid}`}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyLink}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Anyone with this link can view this asset
                  </p>
                </div>

                <Separator />
              </>
            ) : null}

            {/* Shared with others */}
            <div className="space-y-2">
              <h3 className="font-medium">Shared with</h3>

              {/* Filter out public and self from the shared list */}
              {sharingInfo.filter((s: AssetShareInfo) =>
                s.targetType !== 'public' && !(s.targetType === 'user' && s.targetId === user?.id)
              ).length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  This asset is not shared with anyone else
                </p>
              ) : (
                <div className="space-y-2">
                  {sharingInfo
                    .filter((s: AssetShareInfo) =>
                      s.targetType !== 'public' && !(s.targetType === 'user' && s.targetId === user?.id)
                    )
                    .map((share: AssetShareInfo) => {
                    const identifier = `${share.targetType}-${share.targetId}`;
                    return (
                      <div key={identifier} className="flex items-center justify-between p-3 border rounded-md">
                        <div className="flex items-center gap-2">
                          {getTargetIcon(share.targetType)}
                          <div>
                            <div className="font-medium">{getTargetDisplay(share)}</div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {share.targetType} • Shared as {getRoleLabel(share.role)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={getRoleBadgeColor(share.role)}>
                            {getRoleLabel(share.role)}
                          </Badge>
                          {asset.permission?.role === 'owner' && (
                            <>
                              {isOwnOwnerAccess(share) ? (
                                <Badge variant="outline" className="text-xs">
                                  You
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRemoveShareClick(share)}
                                  disabled={removingShareId === identifier}
                                >
                                  {removingShareId === identifier ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* Confirmation Dialog for removing share */}
      <AlertDialog open={shareToRemove !== null} onOpenChange={(open) => !open && setShareToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove sharing access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove sharing access for this {shareToRemove?.targetType}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export { AssetSharingPanel };